import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Login failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--ink)" }}
    >
      <div className="w-full max-w-md">
        <div className="overline mb-6">Corpora · Admin</div>
        <h1 className="font-serif text-5xl font-light leading-tight text-[var(--bone)] mb-2">
          Sign in.
        </h1>
        <p className="text-[var(--mute)] mb-12 leading-relaxed">
          Restricted area. For Corpora editorial staff only.
        </p>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field"
              autoComplete="username"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field"
              autoComplete="current-password"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <div
              className="font-mono text-xs uppercase tracking-[0.18em] py-2"
              style={{ color: "#E07A7A" }}
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
